import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { calendarImport } from '../../api';
import { parseBookingsCsv } from '../../utils/playtomicCsv';
import * as iphoneSync from '../../utils/importedLessonsSync';
import './PlaytomicImport.css';

const SG = { timeZone: 'Asia/Singapore' };
const when = (iso) => new Date(iso).toLocaleString('en-GB', { ...SG, weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const dayOnly = (key) => new Date(`${key}T00:00:00Z`).toLocaleDateString('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric' });
const calendarLabel = { google: 'Google Calendar', iphone: 'iPhone calendar', none: 'no calendar linked' };

const Sample = ({ items, label, tone }) => {
  const [all, setAll] = useState(false);
  if (!items || items.length === 0) return null;
  const shown = all ? items : items.slice(0, 6);
  return (
    <div style={{ marginTop: 10 }}>
      <span className={`pi-chip ${tone}`}>{label}</span>
      <ul className="pi-list">
        {shown.map((it, i) => (
          <li key={i}><time>{when(it.start)}</time><span title={it.title}>{it.title}</span></li>
        ))}
      </ul>
      {items.length > 6 && (
        <button type="button" className="pi-link-btn" onClick={() => setAll(!all)}>{all ? 'Show fewer' : `Show ${items.length - 6} more`}</button>
      )}
    </div>
  );
};

// iPhone only: keep this phone's calendar in step with the lessons imported for me (add, edit, remove)
const IphoneSyncCard = ({ googleWarning, tick, onChanged }) => {
  const [enabled, setEnabled] = useState(iphoneSync.isEnabled());
  const [info, setInfo] = useState(iphoneSync.getInfo());
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { setEnabled(iphoneSync.isEnabled()); setInfo(iphoneSync.getInfo()); }, [tick]);

  const turnOn = async () => {
    setBusy('on'); setError('');
    try { setInfo(await iphoneSync.connect()); setEnabled(true); onChanged(); }
    catch (e) { setError(e && e.message ? e.message : 'Could not turn this on.'); }
    finally { setBusy(''); }
  };
  const update = async () => {
    setBusy('update'); setError('');
    try { setInfo(await iphoneSync.syncNow()); }
    catch (e) { setError(e && e.message ? e.message : 'Could not update the calendar.'); }
    finally { setBusy(''); }
  };
  const turnOff = async () => {
    const remove = window.confirm("Also take the imported lessons off this iPhone's calendar?\n\nOK = remove them too\nCancel = keep them, just stop updating");
    setBusy('off'); setError('');
    try { await iphoneSync.disable(remove); setEnabled(false); setInfo(iphoneSync.getInfo()); }
    catch (e) { setError('Could not turn this off.'); }
    finally { setBusy(''); }
  };

  return (
    <div className="pi-card">
      <h3 className="pi-h">This iPhone's calendar</h3>
      {enabled ? (
        <>
          <div className="pi-ok">
            On. Imported lessons are added to this iPhone's calendar, changed when they change, and removed when they are cancelled or undone.
            {info ? ` Last updated ${new Date(info.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}: ${info.tracked} lessons kept in step.` : ''}
          </div>
          <div className="pi-actions">
            <button type="button" className="submit-button" disabled={busy !== ''} onClick={update}>{busy === 'update' ? 'Updating...' : 'Update now'}</button>
            <button type="button" className="cancel-button" disabled={busy !== ''} onClick={turnOff}>Turn off</button>
          </div>
        </>
      ) : (
        <>
          <p className="pi-sub">
            Add the lessons imported for you to this iPhone's own calendar, and keep them right: new ones are added, edited ones changed,
            cancelled or undone ones removed. Only events CoachPad added are ever removed.
          </p>
          {googleWarning && (
            <div className="pi-warn">You also use Google Calendar. If this iPhone shows your Google Calendar too, the lessons would appear twice. Only turn this on if your iPhone calendar is separate.</div>
          )}
          <div className="pi-actions">
            <button type="button" className="submit-button" disabled={busy !== ''} onClick={turnOn}>{busy === 'on' ? 'Turning on...' : 'Add imported lessons to this iPhone'}</button>
          </div>
        </>
      )}
      {error && <p className="pi-error">{error}</p>}
    </div>
  );
};

// Which Playtomic coach ID(s) each person may pull from the files. Only the head coach can change them; a coach just sees theirs.
const IdsCard = ({ people, canEdit, onSaved }) => {
  const [drafts, setDrafts] = useState({});   // userId -> text being typed
  const [state, setState] = useState({});     // userId -> 'saving' | 'saved' | error text

  const shown = (p) => (drafts[p._id] !== undefined ? drafts[p._id] : p.playtomicCoachIds.join(', '));

  const save = async (p) => {
    const ids = shown(p).split(/[\s,;]+/).map(x => x.trim()).filter(Boolean);
    setState(prev => ({ ...prev, [p._id]: 'saving' }));
    try {
      await calendarImport.saveCoachIds(p._id, ids);
      setDrafts(prev => { const next = { ...prev }; delete next[p._id]; return next; });
      setState(prev => ({ ...prev, [p._id]: 'saved' }));
      onSaved();
    } catch (err) {
      setState(prev => ({ ...prev, [p._id]: err?.response?.data?.error || 'Could not save this.' }));
    }
  };

  if (!canEdit) {
    const me = people.find(p => p.isYou) || people[0];
    const mine = me ? me.playtomicCoachIds : [];
    return (
      <div className="pi-card">
        <h3 className="pi-h">Your Playtomic coach ID</h3>
        {mine.length > 0 ? (
          <>
            <div className="pi-coach-id" style={{ fontSize: 18, marginTop: 4 }}>{mine.join(', ')}</div>
            <p className="pi-note">Only the bookings with this ID are taken from the files. Your head coach sets it; you can't change it.</p>
          </>
        ) : (
          <div className="pi-warn">Your head coach hasn't set your Playtomic coach ID yet, so nothing can be imported. Ask them to add it.</div>
        )}
      </div>
    );
  }

  return (
    <div className="pi-card">
      <h3 className="pi-h">Playtomic coach IDs</h3>
      <p className="pi-sub">
        Set the Playtomic coach ID of each person (more than one, separated by commas, if they have several). A coach can then
        import only the bookings with their own ID, and can't change it. The same ID can go to two people, for example a coach
        covering for another: both then get those bookings in their own calendar.
      </p>
      {people.map(p => (
        <div key={p._id} className="pi-coach">
          <div className="pi-coach-top">
            <span className="pi-person-name">{p.isYou ? `${p.name} (you)` : p.name}</span>
            <span className="pi-file-meta">{calendarLabel[p.calendar]}</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="pi-select"
              inputMode="numeric"
              placeholder="e.g. 9434028"
              value={shown(p)}
              onChange={(e) => { setDrafts(prev => ({ ...prev, [p._id]: e.target.value })); setState(prev => ({ ...prev, [p._id]: '' })); }}
            />
            <button
              type="button"
              className="submit-button"
              style={{ flex: '0 0 auto', padding: '0 16px' }}
              disabled={state[p._id] === 'saving' || drafts[p._id] === undefined}
              onClick={() => save(p)}
            >
              {state[p._id] === 'saving' ? '...' : 'Save'}
            </button>
          </div>
          {state[p._id] === 'saved' && drafts[p._id] === undefined && <span className="pi-file-meta">Saved</span>}
          {state[p._id] && !['saving', 'saved'].includes(state[p._id]) && <span className="pi-error" style={{ margin: 0 }}>{state[p._id]}</span>}
        </div>
      ))}
    </div>
  );
};

// Coach / head coach: drop Playtomic booking exports (CSV), choose whose bookings to take, check them, add them to the calendar.
const PlaytomicImportTab = () => {
  const [people, setPeople] = useState(null);
  const [files, setFiles] = useState([]);
  const [canEdit, setCanEdit] = useState(false);   // only the head coach edits Playtomic coach IDs
  const [unticked, setUnticked] = useState([]);      // head coach: people left out of this import
  const [skipBuffer, setSkipBuffer] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState('');             // 'preview' | 'commit' | ''
  const [error, setError] = useState('');
  const [over, setOver] = useState(false);
  const [syncTick, setSyncTick] = useState(0);
  const inputRef = useRef(null);
  const onIphone = iphoneSync.isAvailable();
  const mePerson = (people || []).find(p => p.isYou);

  // after an import or undo, mirror it into this phone's calendar straight away (when that is switched on)
  const mirrorToPhone = useCallback(() => {
    if (iphoneSync.isAvailable() && iphoneSync.isEnabled()) iphoneSync.syncNow().then(() => setSyncTick(t => t + 1)).catch(() => {});
  }, []);

  const loadHistory = useCallback(() => calendarImport.getHistory().then(res => setHistory(res.data.batches || [])).catch(() => {}), []);

  const loadConfig = useCallback(() => calendarImport.getConfig()
    .then(res => { setPeople(res.data.people || []); setCanEdit(!!res.data.canEdit); })
    .catch(() => { setPeople(prev => prev || []); setError('Could not load your import settings.'); }), []);

  useEffect(() => {
    loadConfig();
    loadHistory();
  }, [loadConfig, loadHistory]);

  const rows = useMemo(() => files.flatMap(f => f.rows), [files]);

  // The Playtomic coach IDs found in the files, with how many bookings and a few customer names to recognise them by
  const detected = useMemo(() => {
    const map = new Map();
    rows.forEach(r => {
      if (!r.coachId) return;
      const d = map.get(r.coachId) || { id: r.coachId, total: 0, active: 0, names: new Set() };
      d.total += 1;
      if (!r.canceled && r.status.toUpperCase() !== 'CANCELED') {
        d.active += 1;
        const label = r.names.join(' & ');
        if (label && !/buffer/i.test(label) && d.names.size < 3) d.names.add(label);
      }
      map.set(r.coachId, d);
    });
    return [...map.values()].sort((a, b) => b.active - a.active);
  }, [rows]);

  const invalidate = () => { setPreview(null); setResult(null); };

  const addFiles = async (fileList) => {
    setError('');
    invalidate();
    const added = [];
    for (const file of Array.from(fileList || [])) {
      if (!/\.csv$/i.test(file.name) && !/csv|text\/plain/i.test(file.type)) {
        added.push({ name: file.name, rows: [], error: 'Not a CSV file.' });
        continue;
      }
      try {
        const parsed = parseBookingsCsv(await file.text());
        const starts = parsed.rows.map(r => r.start).filter(Boolean).sort();
        added.push({ name: file.name, rows: parsed.rows, error: parsed.error, from: starts[0] && starts[0].slice(0, 10), to: starts.length ? starts[starts.length - 1].slice(0, 10) : '' });
      } catch (e) {
        added.push({ name: file.name, rows: [], error: 'Could not read this file.' });
      }
    }
    setFiles(prev => {
      const names = new Set(added.map(f => f.name));
      return [...prev.filter(f => !names.has(f.name)), ...added]; // choosing the same file again replaces it
    });
  };

  const removeFile = (name) => { setFiles(prev => prev.filter(f => f.name !== name)); invalidate(); };

  // Each detected ID belongs to whoever the head coach assigned it to (or to nobody)
  const ownersOf = useCallback((id) => (people || []).filter(p => p.playtomicCoachIds.includes(id)), [people]);
  const matched = useMemo(() => (people || []).filter(p => detected.some(d => p.playtomicCoachIds.includes(d.id))), [people, detected]);
  const recipients = matched.filter(p => !unticked.includes(p._id));

  const assignDetected = async (id, userId) => {
    const person = people.find(p => p._id === userId);
    if (!person) return;
    setError('');
    try {
      await calendarImport.saveCoachIds(userId, [...new Set([...person.playtomicCoachIds, id])]);
      invalidate();
      loadConfig();
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not assign that ID.');
    }
  };

  // the server decides who may pull which Playtomic coach ID; this only says who to include (head coach)
  const payload = () => ({ rows, skipBuffer, userIds: recipients.map(p => p._id), fileNames: files.filter(f => !f.error).map(f => f.name) });

  const runPreview = async () => {
    setBusy('preview'); setError(''); setResult(null);
    try {
      const res = await calendarImport.preview(payload());
      setPreview(res.data);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not check the bookings. Try again.');
    } finally { setBusy(''); }
  };

  const runCommit = async () => {
    setBusy('commit'); setError('');
    try {
      const res = await calendarImport.commit(payload());
      setResult(res.data);
      setPreview(null);
      setFiles([]);
      loadHistory();
      mirrorToPhone();
      loadConfig();
    } catch (err) {
      setError(err?.response?.data?.error || 'The import did not finish. Check the calendar, then try again.');
    } finally { setBusy(''); }
  };

  const undo = async (batch) => {
    const made = (batch.targets || []).reduce((n, t) => n + t.created, 0);
    if (!window.confirm(`Remove the ${made} lessons this import added? Lessons it only updated stay as they are.`)) return;
    setBusy('undo'); setError('');
    try {
      await calendarImport.undo(batch._id);
      loadHistory();
      mirrorToPhone();
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not undo that import.');
    } finally { setBusy(''); }
  };

  const nothingToDo = preview && preview.targets.every(t => t.counts.new + t.counts.changed + t.counts.cancelled === 0);
  const okFiles = files.filter(f => !f.error);

  return (
    <div className="pi">
      <div className="pi-card">
        <h3 className="pi-h">Add Playtomic bookings to the calendar</h3>
        <p className="pi-sub">
          Drop one or more booking exports (CSV) from Playtomic, check them, then add them. Only the bookings with the
          Playtomic coach ID assigned to each person are taken. Times are read as Singapore time.
        </p>

        <div
          className={`pi-drop${over ? ' over' : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current && inputRef.current.click()}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current && inputRef.current.click(); }}
          onDragOver={(e) => { e.preventDefault(); setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => { e.preventDefault(); setOver(false); addFiles(e.dataTransfer.files); }}
        >
          <b>Choose CSV files</b>
          <span>or drop them here. You can pick several at once.</span>
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
          />
        </div>

        {files.length > 0 && (
          <div style={{ marginTop: 10 }}>
            {files.map(f => (
              <div key={f.name} className="pi-file">
                <div className="pi-file-main">
                  <div className="pi-file-name">{f.name}</div>
                  <div className="pi-file-meta">
                    {f.error ? <span className="pi-error" style={{ margin: 0 }}>{f.error}</span>
                      : `${f.rows.length} bookings${f.from ? `, ${dayOnly(f.from)} to ${dayOnly(f.to)}` : ''}`}
                  </div>
                </div>
                <button type="button" className="pi-x" onClick={() => removeFile(f.name)} aria-label={`Remove ${f.name}`}>✕</button>
              </div>
            ))}
          </div>
        )}
        {error && <p className="pi-error">{error}</p>}
      </div>

      {people && <IdsCard people={people} canEdit={canEdit} onSaved={() => { invalidate(); loadConfig(); }} />}

      {onIphone && (
        <IphoneSyncCard googleWarning={!!mePerson && mePerson.calendar === 'google'} tick={syncTick} onChanged={() => setSyncTick(t => t + 1)} />
      )}

      {okFiles.length > 0 && people && (
        <div className="pi-card">
          <h3 className="pi-h">What is in these files</h3>
          <p className="pi-sub">
            Bookings are matched to people by their Playtomic coach ID. Only the IDs assigned above are added; anything else is skipped.
          </p>
          {detected.length === 0 && <p className="pi-note">These files have no coach IDs.</p>}
          {detected.map(d => {
            const owners = ownersOf(d.id);
            return (
              <div key={d.id} className="pi-coach">
                <div className="pi-coach-top">
                  <span className="pi-coach-id">Playtomic coach {d.id}</span>
                  <span className="pi-file-meta">{d.active} bookings{d.total > d.active ? ` (+${d.total - d.active} cancelled)` : ''}</span>
                </div>
                {d.names.size > 0 && <div className="pi-file-meta">e.g. {[...d.names].join(', ')}</div>}
                {owners.length > 0 ? (
                  <div className="pi-ok" style={{ marginTop: 0 }}>Added to {owners.map(o => (o.isYou ? 'your own calendar' : o.name)).join(' and ')}</div>
                ) : canEdit ? (
                  <select className="pi-select" value="" onChange={(e) => assignDetected(d.id, e.target.value)}>
                    <option value="">Not assigned - skipped. Assign to...</option>
                    {people.map(p => <option key={p._id} value={p._id}>{p.isYou ? `Me (${p.name})` : p.name}</option>)}
                  </select>
                ) : (
                  <div className="pi-file-meta">Not yours, skipped.</div>
                )}
              </div>
            );
          })}

          {canEdit && matched.length > 1 && (
            <div style={{ marginTop: 12 }}>
              <div className="pi-file-meta" style={{ marginBottom: 4 }}>Add for:</div>
              {matched.map(p => (
                <label key={p._id} className="pi-check" style={{ marginTop: 6 }}>
                  <input
                    type="checkbox"
                    checked={!unticked.includes(p._id)}
                    onChange={(e) => { setUnticked(prev => (e.target.checked ? prev.filter(x => x !== p._id) : [...prev, p._id])); invalidate(); }}
                  />
                  <span>{p.isYou ? `Me (${p.name})` : p.name}</span>
                </label>
              ))}
            </div>
          )}

          <label className="pi-check">
            <input type="checkbox" checked={skipBuffer} onChange={(e) => { setSkipBuffer(e.target.checked); invalidate(); }} />
            <span>Leave out "buffer" bookings (blocks the coach reserved without a customer)</span>
          </label>
          <p className="pi-note">
            Cancelled bookings are never added; if one was added before, it is removed. Every event written to a Google Calendar gets a Google Meet link.
            Coaches without Google Calendar get the lessons in CoachPad's schedule, where their head coach and managers see them too.
          </p>

          <div className="pi-actions">
            <button type="button" className="submit-button" disabled={busy !== '' || recipients.length === 0} onClick={runPreview}>
              {busy === 'preview' ? 'Checking...' : 'Check bookings'}
            </button>
          </div>
        </div>
      )}

      {preview && (
        <div className="pi-card">
          <h3 className="pi-h">What will happen</h3>
          {preview.targets.map(t => (
            <div key={t.userId} className="pi-person">
              <div className="pi-person-name">{t.name}</div>
              <div className="pi-chips">
                <span className="pi-chip new">{t.counts.new} new</span>
                {t.counts.changed > 0 && <span className="pi-chip change">{t.counts.changed} updated</span>}
                {t.counts.cancelled > 0 && <span className="pi-chip cancel">{t.counts.cancelled} removed</span>}
                <span className="pi-chip">{t.counts.unchanged} already there</span>
                {t.counts.skippedCancelled > 0 && <span className="pi-chip">{t.counts.skippedCancelled} cancelled, skipped</span>}
                {t.counts.skippedBuffer > 0 && <span className="pi-chip">{t.counts.skippedBuffer} buffer, skipped</span>}
                {t.counts.duplicates > 0 && <span className="pi-chip">{t.counts.duplicates} duplicates</span>}
                {t.counts.invalid > 0 && <span className="pi-chip cancel">{t.counts.invalid} unreadable</span>}
              </div>
              <Sample items={t.sample.new} label="New" tone="new" />
              <Sample items={t.sample.change} label="Updated" tone="change" />
              <Sample items={t.sample.cancel} label="Removed" tone="cancel" />
              {t.unknownVenues.length > 0 && (
                <div className="pi-warn">Some bookings are at a venue CoachPad doesn't have an address for yet ({t.unknownVenues.join(', ')}). They are added without an address.</div>
              )}
            </div>
          ))}
          {Object.keys(preview.unmatchedCoachIds).length > 0 && (
            <p className="pi-note">Skipped, not matched to anyone: {Object.entries(preview.unmatchedCoachIds).map(([id, n]) => `coach ${id} (${n})`).join(', ')}.</p>
          )}
          <div className="pi-actions">
            <button type="button" className="submit-button" disabled={busy !== '' || nothingToDo} onClick={runCommit}>
              {busy === 'commit' ? 'Adding... this can take a minute' : nothingToDo ? 'Nothing to add' : 'Add to calendar'}
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="pi-card">
          <h3 className="pi-h">Done</h3>
          {result.results.map(r => (
            <div key={r.userId} className="pi-person">
              <div className="pi-person-name">{r.name}</div>
              <div className="pi-chips">
                <span className="pi-chip new">{r.created} added</span>
                {r.updated > 0 && <span className="pi-chip change">{r.updated} updated</span>}
                {r.cancelled > 0 && <span className="pi-chip cancel">{r.cancelled} removed</span>}
              </div>
              {r.googleConnected ? (
                <div className="pi-ok">
                  Written to Google Calendar: {r.googleWritten}. Google Meet links added: {r.meetLinks}.
                  {r.meetMissing > 0 && ` ${r.meetMissing} without a Meet link (this calendar cannot create them).`}
                </div>
              ) : (
                <div className="pi-note">Saved in CoachPad. This person has no Google Calendar linked, so the lessons show in the CoachPad schedule.</div>
              )}
              {r.googleFailed > 0 && <div className="pi-warn">{r.googleFailed} could not be written to Google Calendar (they are still in CoachPad): {r.errors.join(' | ')}</div>}
              {r.googleFailed === 0 && r.errors.length > 0 && <div className="pi-warn">{r.errors.join(' | ')}</div>}
            </div>
          ))}
        </div>
      )}

      {history.length > 0 && (
        <div className="pi-card">
          <h3 className="pi-h">Recent imports</h3>
          {history.map(b => {
            const made = (b.targets || []).reduce((n, t) => n + t.created, 0);
            const updated = (b.targets || []).reduce((n, t) => n + t.updated, 0);
            return (
              <div key={b._id} className="pi-hist">
                <span>
                  {new Date(b.createdAt).toLocaleString('en-GB', { ...SG, day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  {' - '}{made} added{updated ? `, ${updated} updated` : ''}
                  {(b.targets || []).length > 1 ? ` (${b.targets.map(t => t.name).join(', ')})` : b.targets && b.targets[0] ? ` (${b.targets[0].name})` : ''}
                </span>
                {b.undone ? <span className="pi-file-meta">undone</span>
                  : made > 0 && <button type="button" className="pi-link-btn" disabled={busy !== ''} onClick={() => undo(b)}>Undo</button>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PlaytomicImportTab;
